import type { BodyBlock, Group, Statute, Unit } from './types';
import type { StatutePreviewUiSection } from '../../components/StatutePreviewReader';

/**
 * Bridge: canonical Statute -> existing reader view-model.
 *
 * The reader consumes a flat StatutePreviewUiSection[] keyed by id, with
 * grouping derived from `tocPartLabel`. StatuteRenderer reads
 * `content`/`subsections`/`clauses`/`provisos`/`explanations` off each row
 * directly, so we must emit those legacy fields in addition to the structured
 * `body` (kept for diagnostics / future renderers). This keeps zero visual
 * regression for CPC and works uniformly across all four format families.
 */

export type CanonicalUiSection = StatutePreviewUiSection & {
  body?: BodyBlock[];
  groupKind?: Group['kind'];
  unitKind?: Unit['kind'];
};

/** Legacy section shape recognized by StatuteRenderer (`./StatuteRenderer.tsx`). */
type LegacyChild = {
  id?: string;
  number?: string;
  letter?: string;
  content?: string;
  subsections?: LegacyChild[];
  clauses?: LegacyChild[];
  provisos?: string[];
  explanations?: string[];
};

/** label + " " + title (space, to match existing splitPartHeading regex). */
function joinHeading(label: string, title?: string): string {
  const l = (label || '').trim();
  const t = (title || '').trim();
  if (l && t && l.toLowerCase() !== t.toLowerCase()) return `${l} ${t}`;
  return l || t;
}

function normalizeForCompare(value: string): string {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function dropDuplicateTitleFromBody(title: string, body: BodyBlock[]): BodyBlock[] {
  if (!Array.isArray(body) || body.length === 0) return body;
  const first = body[0];
  if (first.kind !== 'paragraph') return body;
  const firstText = normalizeForCompare(first.text);
  const headingText = normalizeForCompare(title);
  if (!firstText || !headingText) return body;
  if (firstText !== headingText) return body;
  return body.slice(1);
}

function bodyChildrenToLegacy(children: BodyBlock[] | undefined) {
  const clauses: LegacyChild[] = [];
  const provisos: string[] = [];
  const explanations: string[] = [];
  if (!children?.length) return { clauses, provisos, explanations };
  for (const child of children) {
    if (child.kind === 'clause') {
      clauses.push({ letter: child.letter, content: child.text });
    } else if (child.kind === 'proviso') {
      provisos.push(child.text);
    } else if (child.kind === 'explanation') {
      explanations.push(child.text);
    }
  }
  return { clauses, provisos, explanations };
}

/**
 * Convert canonical body blocks into the `content`/`subsections`/... shape
 * that StatuteRenderer already knows how to render.
 */
function bodyToLegacyShape(body: BodyBlock[]): {
  content?: string;
  subsections?: LegacyChild[];
  clauses?: LegacyChild[];
  provisos?: string[];
  explanations?: string[];
} {
  const lead: string[] = [];
  const subsections: LegacyChild[] = [];
  const topClauses: LegacyChild[] = [];
  const topProvisos: string[] = [];
  const topExplanations: string[] = [];
  const illustrations: string[] = [];

  for (const block of body) {
    if (block.kind === 'paragraph') {
      lead.push(block.text);
    } else if (block.kind === 'subsection') {
      const { clauses, provisos, explanations } = bodyChildrenToLegacy(block.children);
      const sub: LegacyChild = {
        number: block.number,
        content: block.text,
      };
      if (clauses.length) sub.clauses = clauses;
      if (provisos.length) sub.provisos = provisos;
      if (explanations.length) sub.explanations = explanations;
      subsections.push(sub);
    } else if (block.kind === 'clause') {
      topClauses.push({ letter: block.letter, content: block.text });
    } else if (block.kind === 'proviso') {
      topProvisos.push(block.text);
    } else if (block.kind === 'explanation') {
      topExplanations.push(block.text);
    } else if (block.kind === 'illustration') {
      illustrations.push(`Illustration: ${block.text}`);
    }
  }

  const content = [lead.join('\n').trim(), illustrations.join('\n').trim()].filter(Boolean).join('\n\n').trim();

  const out: ReturnType<typeof bodyToLegacyShape> = {};
  if (content) out.content = content;
  if (subsections.length) out.subsections = subsections;
  if (topClauses.length) out.clauses = topClauses;
  if (topProvisos.length) out.provisos = topProvisos;
  if (topExplanations.length) out.explanations = topExplanations;
  return out;
}

function tocSectionBadgeFor(unit: Unit, group: Group): string {
  switch (unit.kind) {
    case 'article':
      return `Article ${unit.number}`;
    case 'rule': {
      const orderLabel = (group.label || '').trim();
      const orderMatch = orderLabel.match(/^Order\s+([A-Za-z0-9]+)$/i);
      if (orderMatch) return `Order ${orderMatch[1]}, Rule ${unit.number}`;
      return `${orderLabel ? `${orderLabel}, ` : ''}Rule ${unit.number}`;
    }
    case 'form': {
      const appLabel = (group.label || '').trim();
      const appMatch = appLabel.match(/^Appendix\s+([A-Za-z0-9]+)$/i);
      if (appMatch) return `Appendix ${appMatch[1]}, Form ${unit.number}`;
      return `${appLabel ? `${appLabel}, ` : ''}Form ${unit.number}`;
    }
    case 'section':
    default:
      if (group.kind === 'schedule') {
        const schMatch = (group.label || '').match(/(\d+)/);
        if (schMatch) return `Schedule ${schMatch[1]}, Section ${unit.number}`;
      }
      return `Section ${unit.number}`;
  }
}

function tocPartLabelFor(unit: Unit, group: Group): string | undefined {
  if (group.kind === 'chapter') {
    if (group.ancestors && group.ancestors.length > 0) return group.ancestors[0];
    return joinHeading(group.label, group.title) || undefined;
  }
  return joinHeading(group.label, group.title) || undefined;
}

function tocChapterLabelFor(_unit: Unit, group: Group): string | undefined {
  if (group.kind === 'chapter') return joinHeading(group.label, group.title) || undefined;
  return undefined;
}

function preambleSection(statute: Statute): CanonicalUiSection | null {
  if (!statute.preamble) return null;
  const { id, title, body } = statute.preamble;
  const dedupedBody = dropDuplicateTitleFromBody(title, body);
  const legacy = bodyToLegacyShape(dedupedBody);
  return {
    id,
    label: 'Preamble',
    title,
    shortTitle: title,
    tocLine: title,
    tocPartLabel: 'Preamble',
    tocSectionBadge: 'Preamble',
    body: dedupedBody,
    unitKind: 'section',
    ...(legacy.content !== undefined ? { content: legacy.content || title } : { content: title }),
    ...(legacy.subsections ? { subsections: legacy.subsections } : {}),
    ...(legacy.clauses ? { clauses: legacy.clauses } : {}),
    ...(legacy.provisos ? { provisos: legacy.provisos } : {}),
    ...(legacy.explanations ? { explanations: legacy.explanations } : {}),
    order: 0,
  };
}

export function toPreviewSections(statute: Statute): CanonicalUiSection[] {
  const out: CanonicalUiSection[] = [];
  let order = 0;

  const preamble = preambleSection(statute);
  if (preamble) {
    preamble.order = order++;
    out.push(preamble);
  }

  for (const group of statute.groups) {
    for (const unit of group.units) {
      const tocPartLabel = tocPartLabelFor(unit, group);
      const tocChapterLabel = tocChapterLabelFor(unit, group);
      const tocSectionBadge = tocSectionBadgeFor(unit, group);
      const shortTitle = unit.title || unit.numberDisplay || `${unit.kind} ${unit.number}`;
      const trailParts = [tocPartLabel, tocChapterLabel, shortTitle].filter(Boolean) as string[];
      const dedupedBody = dropDuplicateTitleFromBody(shortTitle, unit.body);
      const legacy = bodyToLegacyShape(dedupedBody);
      const hasStructuredBody = Boolean(
        (legacy.subsections && legacy.subsections.length > 0) ||
          (legacy.clauses && legacy.clauses.length > 0) ||
          (legacy.provisos && legacy.provisos.length > 0) ||
          (legacy.explanations && legacy.explanations.length > 0),
      );
      const contentValue =
        legacy.content !== undefined ? legacy.content : hasStructuredBody ? undefined : shortTitle;

      out.push({
        id: unit.id,
        label: unit.numberDisplay || `${unit.kind} ${unit.number}`,
        title: shortTitle,
        shortTitle,
        tocLine: trailParts.join(' › '),
        ...(tocPartLabel ? { tocPartLabel } : {}),
        ...(tocChapterLabel ? { tocChapterLabel } : {}),
        ...(tocSectionBadge ? { tocSectionBadge } : {}),
        ...(contentValue !== undefined ? { content: contentValue } : {}),
        ...(legacy.subsections ? { subsections: legacy.subsections } : {}),
        ...(legacy.clauses ? { clauses: legacy.clauses } : {}),
        ...(legacy.provisos ? { provisos: legacy.provisos } : {}),
        ...(legacy.explanations ? { explanations: legacy.explanations } : {}),
        body: dedupedBody,
        order: order++,
        unitKind: unit.kind,
        groupKind: group.kind,
      });
    }
  }

  return out.map((section, i) => ({ ...section, tocListIndex: i + 1 }));
}
